import type { SupabaseClient } from '@supabase/supabase-js'
import { jobMarginFull } from './margin'

/**
 * Slice 4 CP2 — freeze a job-sourced invoice's cost basis into
 * invoice_cost_snapshots at conversion time. ONE row per invoice (UNIQUE
 * invoice_id); cost is summed across ALL covered jobs — the consolidated
 * BillingTab path passes many jobIds, the per-job paths pass one.
 *
 * BEST-EFFORT / NON-FATAL: the entire body is wrapped in try/catch — on ANY
 * error it console.errors and returns. The caller never depends on the result;
 * a snapshot failure must NOT break invoice creation (mirrors the
 * healthcheck-ping / processor_runs non-fatal writers). Call it AFTER the
 * invoice insert + job-link SUCCEED, never inside a void-on-failure path.
 *
 * Confidentiality: invoice_cost_snapshots is staff-only (workspace_isolation
 * RLS, no portal/vendor) — cost never reaches the customer. See the CP1
 * migration (20260623040000).
 *
 * The client is typed permissively (SupabaseClient with default generics)
 * because invoice_cost_snapshots is a new table not yet in the generated
 * Database types; the typed singleton is assignable to it.
 */
export async function snapshotInvoiceCost(
  client: SupabaseClient,
  workspaceId: string,
  invoiceId: string,
  jobIds: string[],
): Promise<void> {
  try {
    if (!jobIds.length) return

    // 1. Raw line items across all covered jobs — unit_cost IS needed for the
    //    margin (unlike the invoice jsonb, which strips cost).
    const { data: lineRows, error: lineErr } = await client
      .from('field_job_line_items')
      .select('unit_price, qty, unit_cost')
      .in('field_job_id', jobIds)
    if (lineErr) throw lineErr
    const lines = (lineRows ?? []).map((r: any) => ({
      unit_price: Number(r.unit_price) || 0,
      qty: Number(r.qty) || 0,
      unit_cost: r.unit_cost ?? null,
    }))

    // 2. vendorActual = Σ vendor_bills.amount across the jobs; null when zero
    //    bills (unknown, ≠ 0).
    const { data: billRows, error: billErr } = await client
      .from('vendor_bills')
      .select('amount')
      .in('field_job_id', jobIds)
    if (billErr) throw billErr
    const vendorActual = (!billRows || billRows.length === 0)
      ? null
      : billRows.reduce((sum: number, b: any) => sum + (Number(b.amount) || 0), 0)

    // 3. laborCost = Σ field_job_labor.amount across the jobs; null when none
    //    (N/A, ≠ 0 — labor is never "unknown").
    const { data: laborRows, error: laborErr } = await client
      .from('field_job_labor')
      .select('amount')
      .in('field_job_id', jobIds)
    if (laborErr) throw laborErr
    const laborCost = (!laborRows || laborRows.length === 0)
      ? null
      : laborRows.reduce((sum: number, l: any) => sum + (Number(l.amount) || 0), 0)

    // 4. hasVendorAssignments — does ANY covered job have a vendor assignment?
    const { data: assignRows, error: assignErr } = await client
      .from('vendor_assignments')
      .select('id')
      .in('field_job_id', jobIds)
      .limit(1)
    if (assignErr) throw assignErr
    const hasVendorAssignments = !!(assignRows && assignRows.length > 0)

    // 5. Compute the SAME margin the job-side shows (compute-on-read parity).
    //    COARSE multi-job flag: across a consolidated invoice, vendorCostUnknown
    //    is approximate — if SOME but not all covered jobs are subbed-unbilled,
    //    the summed vendorActual may be non-null and MASK one job's unknown
    //    vendor cost. Accepted approximation — it matches jobMarginFull's
    //    "has assignments AND vendorActual==null" flag semantics (per-computation,
    //    not per-job).
    const m = jobMarginFull(lines, vendorActual, laborCost, { hasVendorAssignments })

    // 6. Upsert ONE row. onConflict invoice_id → a retry / re-conversion UPDATEs
    //    the existing row rather than duplicating (invoice_id is UNIQUE).
    const { error: upsertErr } = await client
      .from('invoice_cost_snapshots')
      .upsert({
        workspace_id: workspaceId,
        invoice_id: invoiceId,
        revenue: m.revenue,
        line_cost: m.lineCost,
        vendor_actual: m.vendorActual,
        labor_cost: m.laborCost,
        line_costs_partial: m.lineCostsPartial,
        vendor_cost_unknown: m.vendorCostUnknown,
        has_vendor_assignments: m.hasVendorAssignments,
      }, { onConflict: 'invoice_id' })
    if (upsertErr) throw upsertErr
  } catch (err) {
    // Non-fatal: a snapshot failure must NEVER break invoice creation.
    console.error('snapshotInvoiceCost failed (non-fatal):', err)
  }
}
